import React, {
  useRef,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { Property } from "../types";
import PropertyCard from "./PropertyCard";
import { HoverStore, useIsHovered } from "../lib/hoverStore";
import { ChangeKind } from "../lib/changes";

export interface PropertyListHandle {
  scrollToCard: (id: string) => void;
}

interface EmptyStateConfig {
  icon: string;
  title: string;
  desc: string;
  cta?: { label: string; onClick: () => void };
}

interface PropertyListProps {
  allProperties: Property[];
  filteredProperties: Property[];
  selectedProperty: Property | null;
  onSelectProperty: (property: Property | null) => void;
  authed: boolean;
  // isAllowedCounty — geography-aware tier gate (Free/Scout redesign). A
  // function (not a bare boolean) since it must be evaluated PER property —
  // a free/scout user may have full access to some rows and a fuzzed view
  // of others in the same list, depending on each row's county.
  isAllowedCounty: (property: Property) => boolean;
  watchedIds: Set<string>;
  togglingIds: Set<string>;
  watchLimit: number;
  watchLimitMax: number;
  onToggleWatch: (propertyId: string) => void;
  // "Not interested" (#3074). All optional: a caller that doesn't pass them
  // gets no hide control and no hidden-count toggle.
  hiddenIds?: Set<string>;
  onToggleHidden?: (propertyId: string) => void;
  // List <-> map hover sync (#3077). Optional; see lib/hoverStore.
  hoverStore?: HoverStore;
  // Bulk select (#3079). onToggleSelectMode adds a "Select" toggle to the
  // header; while selectMode is on, cards show checkboxes.
  selectMode?: boolean;
  checkedIds?: Set<string>;
  onToggleChecked?: (id: string) => void;
  onToggleSelectMode?: () => void;
  // Opens the printable auction-week sheet (#3211) from the list header.
  onOpenAuctionSheet?: () => void;
  // Deal stage labels by property id (#3075), watchlist view only.
  stageLabels?: Map<string, string>;
  // Changes since the last visit (#3207): per-id kinds, and the header chip
  // that filters the list to them.
  changeKinds?: Map<string, ChangeKind[]>;
  changedCount?: number;
  changedOnly?: boolean;
  onToggleChangedOnly?: () => void;
  hiddenCount?: number;
  showHidden?: boolean;
  onToggleShowHidden?: () => void;
  onRequireAuth: () => void;
  onRequirePro: () => void;
  // See PropertyCard's onPickCounty: set only for a free/scout viewer with no
  // chosen county, so the fuzzed CTA opens the county picker.
  onPickCounty?: () => void;
  countLabelOverride?: string;
  // Optional override for the "no items in this view" empty state —
  // lets App distinguish "watchlist empty" ("add properties to watch")
  // from the generic "no properties loaded" message.
  emptyStateOverride?: EmptyStateConfig;
  // Sort control (2026-09 tab-chrome redesign): moved here from the filter
  // bar so it sits next to the count it actually describes, instead of
  // buried mid-way through an unrelated row of filter selects. Both
  // optional — a caller that doesn't pass them (none currently do, but a
  // future read-only embed might) just gets the count with no sort UI.
  sortField?: string;
  sortFields?: { value: string; label: string }[];
  onSortFieldChange?: (field: string) => void;
  // Direction toggle — the ↑/↓ arrow next to the field select. Hidden when the
  // current field has only one meaningful order (e.g. best deal score).
  sortDirectional?: boolean;
  sortReversed?: boolean;
  sortDirTooltip?: string;
  onToggleSortReversed?: () => void;
  // activeLeadTypeFilter — the lead-type filter currently applied to this
  // list (e.g. "mortgage_foreclosure"), threaded down to each PropertyCard so
  // its headline badge leads with the signal that matched the filter instead
  // of the fixed-priority default. See resolvePropertyBadge in dataQuality.ts.
  activeLeadTypeFilter?: string | string[];
}

// RowContext carries the per-render state each virtualized card needs that
// is NOT part of Virtuoso's `data` array (selection, watch status, the
// callbacks). Passing it via Virtuoso's `context` prop is what makes the
// visible rows re-render when, e.g., the user selects a different property
// or stars one — without those values being in `data`, Virtuoso wouldn't
// otherwise know to re-run itemContent. See #361 virtualization.
interface RowContext {
  selectedId: string | null;
  authed: boolean;
  isAllowedCounty: (property: Property) => boolean;
  watchedIds: Set<string>;
  togglingIds: Set<string>;
  watchLimit: number;
  watchLimitMax: number;
  onSelectProperty: (property: Property | null) => void;
  onToggleWatch: (propertyId: string) => void;
  hiddenIds?: Set<string>;
  onToggleHidden?: (propertyId: string) => void;
  stageLabels?: Map<string, string>;
  changeKinds?: Map<string, ChangeKind[]>;
  hoverStore?: HoverStore;
  checkedIds?: Set<string>;
  onToggleChecked?: (id: string) => void;
  onRequireAuth: () => void;
  onRequirePro: () => void;
  onPickCounty?: () => void;
  activeLeadTypeFilter?: string | string[];
}

// A card that reads its own hover flag from the store (#3077), so a hover
// change re-renders the two cards involved instead of every visible row.
const HoverableCard: React.FC<
  Omit<React.ComponentProps<typeof PropertyCard>, "isHovered" | "onHoverChange"> & {
    hoverStore?: HoverStore;
  }
> = ({ hoverStore, ...props }) => {
  const isHovered = useIsHovered(hoverStore, props.property.id);
  return <PropertyCard {...props} isHovered={isHovered} onHoverChange={hoverStore?.set} />;
};

// PropertyList — VIRTUALIZED (#361). Previously rendered all N PropertyCards
// at once (683 for Mecklenburg), which mounted ~15.5K DOM nodes in a single
// synchronous React commit and blocked the main thread for hundreds of ms to
// multiple seconds ("Chrome not responding"). Confirmed via live longtask
// profiling 2026-05-30: cost scaled linearly with property count (49 props =
// 68ms, 683 = 279-434ms+). react-virtuoso renders only the ~10-20 cards in
// the viewport and recycles them on scroll, so the render cost is constant
// regardless of how many properties the county has.
const PropertyList = forwardRef<PropertyListHandle, PropertyListProps>(
  (
    {
      allProperties,
      filteredProperties,
      selectedProperty,
      onSelectProperty,
      authed,
      isAllowedCounty,
      watchedIds,
      togglingIds,
      watchLimit,
      watchLimitMax,
      onToggleWatch,
      hiddenIds,
      onToggleHidden,
      stageLabels,
      changeKinds,
      changedCount = 0,
      changedOnly = false,
      onToggleChangedOnly,
      hoverStore,
      selectMode = false,
      checkedIds,
      onToggleChecked,
      onToggleSelectMode,
      onOpenAuctionSheet,
      hiddenCount = 0,
      showHidden = false,
      onToggleShowHidden,
      onRequireAuth,
      onRequirePro,
      onPickCounty,
      countLabelOverride,
      emptyStateOverride,
      sortField,
      sortFields,
      onSortFieldChange,
      sortDirectional,
      sortReversed,
      sortDirTooltip,
      onToggleSortReversed,
      activeLeadTypeFilter,
    },
    ref
  ) => {
    // Render only filtered properties (when no filter, filtered === all)
    const displayProperties = filteredProperties;

    // Virtuoso handle drives programmatic scrolling. Replaces the old
    // per-card ref map + scrollIntoView, which can't work once off-screen
    // cards aren't mounted in the DOM.
    const virtuosoRef = useRef<VirtuosoHandle>(null);

    // scrollToCard — called when the user clicks a map pin, to scroll the
    // list to that property. Maps the property id to its index in the
    // current (filtered) list and asks Virtuoso to bring it into view.
    const scrollToCard = useCallback(
      (id: string) => {
        const index = displayProperties.findIndex((p) => p.id === id);
        if (index >= 0) {
          virtuosoRef.current?.scrollToIndex({
            index,
            behavior: "smooth",
            align: "center",
          });
        }
      },
      [displayProperties]
    );

    useImperativeHandle(ref, () => ({ scrollToCard }), [scrollToCard]);

    const isFiltering = filteredProperties.length < allProperties.length;
    const countLabel =
      countLabelOverride ??
      (isFiltering
        ? `${filteredProperties.length} of ${allProperties.length} match`
        : `${allProperties.length} ${allProperties.length === 1 ? "property" : "properties"}`);

    const noResults = isFiltering && filteredProperties.length === 0 && allProperties.length > 0;

    const context = useMemo<RowContext>(
      () => ({
        selectedId: selectedProperty?.id ?? null,
        authed,
        isAllowedCounty,
        watchedIds,
        togglingIds,
        watchLimit,
        watchLimitMax,
        onSelectProperty,
        onToggleWatch,
        hiddenIds,
        onToggleHidden,
        stageLabels,
        changeKinds,
        hoverStore,
        checkedIds: selectMode ? checkedIds : undefined,
        onToggleChecked: selectMode ? onToggleChecked : undefined,
        onRequireAuth,
        onRequirePro,
        onPickCounty,
        activeLeadTypeFilter,
      }),
      [
        hiddenIds,
        onToggleHidden,
        stageLabels,
        changeKinds,
        hoverStore,
        selectMode,
        checkedIds,
        onToggleChecked,
        selectedProperty,
        authed,
        isAllowedCounty,
        watchedIds,
        togglingIds,
        watchLimit,
        watchLimitMax,
        onSelectProperty,
        onToggleWatch,
        onRequireAuth,
        onRequirePro,
        onPickCounty,
        activeLeadTypeFilter,
      ]
    );

    return (
      <div className="property-list fade-in">
        <div className="list-header">
          <div className="list-count">{countLabel}</div>
          {onToggleSelectMode && displayProperties.length > 0 && (
            <button
              type="button"
              className="list-select-toggle"
              aria-pressed={selectMode}
              onClick={onToggleSelectMode}
            >
              {selectMode ? "Cancel select" : "Select"}
            </button>
          )}
          {onToggleChangedOnly && (changedCount > 0 || changedOnly) && (
            <button
              type="button"
              className="list-select-toggle"
              aria-pressed={changedOnly}
              onClick={onToggleChangedOnly}
              title="New properties, new upset bids and status changes since your last visit"
            >
              {changedOnly ? "Show all" : `${changedCount} changed since last visit`}
            </button>
          )}
          {onOpenAuctionSheet && displayProperties.length > 0 && (
            <button
              type="button"
              className="list-select-toggle"
              onClick={onOpenAuctionSheet}
              title="A printable sheet of the next 7 days of sales"
            >
              Auction sheet
            </button>
          )}
          {hiddenCount > 0 && onToggleShowHidden && (
            <button
              type="button"
              className="list-hidden-toggle"
              aria-pressed={showHidden}
              onClick={onToggleShowHidden}
              title="Properties you marked Not interested"
            >
              {showHidden ? `Hide ${hiddenCount} not interested` : `${hiddenCount} hidden · Show`}
            </button>
          )}
          {sortFields && onSortFieldChange && (
            <div className="list-sort-group">
              <select
                className="list-sort"
                value={sortField}
                onChange={(e) => onSortFieldChange(e.target.value)}
                title="Sort properties by"
                aria-label="Sort properties by"
              >
                {sortFields.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {sortDirectional && onToggleSortReversed && (
                <button
                  type="button"
                  className="list-sort-dir"
                  onClick={onToggleSortReversed}
                  title={sortDirTooltip}
                  aria-label={sortDirTooltip}
                >
                  {sortReversed ? "↑" : "↓"}
                </button>
              )}
            </div>
          )}
        </div>
        {noResults && (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <p className="empty-state-title">No properties match your filters</p>
            <p className="empty-state-desc">Try adjusting your search or status filter</p>
          </div>
        )}
        {allProperties.length === 0 && !isFiltering && (
          emptyStateOverride ? (
            <div className="empty-state">
              <div className="empty-state-icon">{emptyStateOverride.icon}</div>
              <p className="empty-state-title">{emptyStateOverride.title}</p>
              <p className="empty-state-desc">{emptyStateOverride.desc}</p>
              {emptyStateOverride.cta && (
                <button
                  className="empty-state-cta"
                  onClick={emptyStateOverride.cta.onClick}
                >
                  {emptyStateOverride.cta.label}
                </button>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🏠</div>
              <p className="empty-state-title">No tracked properties in this county right now</p>
              <p className="empty-state-desc">
                Some NC counties have little or no active foreclosure inventory at a
                given time. Try another county from the selector above — data
                refreshes twice a day.
              </p>
            </div>
          )
        )}
        {displayProperties.length > 0 && (
          <Virtuoso<Property, RowContext>
            ref={virtuosoRef}
            className="property-list__virtuoso"
            data={displayProperties}
            context={context}
            // Stable key per property so Virtuoso recycles correctly across
            // data changes (county switch, filter) without remounting cards
            // that didn't actually change.
            computeItemKey={(_index, property) => property.id}
            // Render a few extra rows above/below the viewport so fast
            // scrolling doesn't flash blank space.
            overscan={400}
            itemContent={(_index, property, ctx) => (
              <HoverableCard
                hoverStore={ctx.hoverStore}
                property={property}
                isSelected={ctx.selectedId === property.id}
                isHighlighted={true}
                onSelect={ctx.onSelectProperty}
                authed={ctx.authed}
                isWatched={ctx.watchedIds.has(property.id)}
                watchLoading={ctx.togglingIds.has(property.id)}
                isAllowedCounty={ctx.isAllowedCounty(property)}
                watchLimit={ctx.watchLimit}
                watchLimitMax={ctx.watchLimitMax}
                onToggleWatch={ctx.onToggleWatch}
                isHidden={ctx.hiddenIds?.has(property.id) ?? false}
                onToggleHidden={ctx.onToggleHidden}
                stageLabel={ctx.stageLabels?.get(property.id)}
                changeKind={ctx.changeKinds?.get(property.id)?.[0]}
                isChecked={ctx.checkedIds?.has(property.id) ?? false}
                onToggleChecked={ctx.onToggleChecked}
                onRequireAuth={ctx.onRequireAuth}
                onRequirePro={ctx.onRequirePro}
                onPickCounty={ctx.onPickCounty}
                activeLeadTypeFilter={ctx.activeLeadTypeFilter}
              />
            )}
          />
        )}
      </div>
    );
  }
);

PropertyList.displayName = "PropertyList";

export default PropertyList;
